import axios from 'axios';

import { notify } from './toast';
import { reportError } from './logError';
import { store } from '../app/store';

// Keys whose values must never be logged.
const SENSITIVE_KEY = /password|secret|token|authorization|jwt|credential|api[-_]?key/i;

/** Recursively redact sensitive fields and bound the size of logged request data. */
function redact(value: unknown, depth = 0): unknown {
  if (value == null || depth > 4) return value;
  if (typeof value === 'string') {
    try {
      return redact(JSON.parse(value), depth); // axios serializes bodies to strings
    } catch {
      return value.length > 500 ? `${value.slice(0, 500)}…` : value;
    }
  }
  if (Array.isArray(value)) return value.slice(0, 25).map((item) => redact(item, depth + 1));
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) => [
        key,
        SENSITIVE_KEY.test(key) ? '[redacted]' : redact(val, depth + 1),
      ]),
    );
  }
  return value;
}

function currentUserEmail(): string | undefined {
  try {
    return (store.getState() as { user?: { user?: { email?: string } } })?.user?.user?.email;
  } catch {
    return undefined;
  }
}

// Per-request opt-outs for the global error handling (set in the axios config of
// calls that handle these statuses themselves, e.g. a 404-tolerant lookup).
declare module 'axios' {
  export interface AxiosRequestConfig {
    suppressErrorToast?: boolean;
    suppressNotFoundToast?: boolean;
    suppressErrorReport?: boolean;
  }
}

const reload = (): void => window.location.reload();

// Throttle duplicate reports so a burst of the same failure doesn't flood CloudWatch.
const REPORT_THROTTLE_MS = 30_000;
const recentReports = new Map<string, number>();

/** Test-only: clears the report throttle. */
export function clearHttpErrorReportThrottle(): void {
  recentReports.clear();
}

function shouldReport(error: any): boolean {
  const config = error?.config ?? {};
  if (config.suppressErrorReport) return false;
  const status: number | undefined = error?.response?.status;
  if (!error?.response) return true; // network / no response
  if (status !== undefined && status >= 500) return true;
  if (status === 404 && !config.suppressNotFoundToast) return true;
  return false; // 400/401/403/etc. are expected — don't report as defects
}

/** Fire-and-forget report of an HTTP error to CloudWatch (so the team knows). */
function reportHttpError(error: any): void {
  if (!shouldReport(error)) return;

  const config = error?.config ?? {};
  const status = error?.response?.status ?? 'no-response';
  const method = (config.method ?? 'get').toUpperCase();
  const url: string = config.url ?? '';

  const key = `${method} ${url} ${status}`;
  const now = Date.now();
  const last = recentReports.get(key);
  if (last && now - last < REPORT_THROTTLE_MS) return;
  recentReports.set(key, now);

  const responseData = error?.response?.data;
  const details = {
    type: 'http-error',
    status,
    method,
    url,
    message: error?.message,
    // The request that failed — so a dev can see/reproduce what was sent.
    request: {
      params: redact(config.params),
      data: redact(config.data),
    },
    responseData: typeof responseData === 'string' ? responseData.slice(0, 500) : responseData,
    user: currentUserEmail(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    location: window.location.href,
    time: new Date().toISOString(),
  };

  const logGroup = 'tpn-client/error-handler';
  const logStream = `${process.env.REACT_APP_ENVIRONMENT?.toLowerCase() || 'dev'}-http-errors`;

  reportError(logGroup, logStream, details);
}

/**
 * Maps a rejected axios error to (1) a user-facing toast and (2) a CloudWatch
 * report so the team knows, honoring per-request opt-outs, then re-rejects so
 * existing component error handling still runs. Exported for tests.
 */
export function handleResponseError(error: any): Promise<never> {
  const config = error?.config ?? {};
  const url: string = config.url ?? '';

  // Guard against recursion: never toast/report failures of the logger itself.
  if (url.includes('/cloudwatch-logging/')) return Promise.reject(error);

  const status: number | undefined = error?.response?.status;

  if (!config.suppressErrorToast) {
    if (!error?.response) {
      notify('Network error — check your connection and try again.', {
        severity: 'error',
        action: { label: 'Reload', onClick: reload },
      });
    } else if (status !== undefined && status >= 500) {
      notify('Something went wrong on our end. Please try again — if it keeps happening, contact support.', {
        severity: 'error',
        action: { label: 'Reload', onClick: reload },
      });
    } else if (status === 404 && !config.suppressNotFoundToast) {
      notify("We couldn't find what you were looking for. It may have been moved or deleted.", {
        severity: 'warning',
      });
    }
  }

  reportHttpError(error);

  // Always reject so existing component/loading/error handling still runs.
  return Promise.reject(error);
}

axios.interceptors.response.use((response) => response, handleResponseError);
